export enum SchemeType {
  PayPerFirstPurchase = 'payPerFirstPurchase',
  PayPerRegistration = 'payPerRegistration',
  PayPerVerifiedRegistration = 'payPerVerifiedRegistration',
  RevenueShare = 'revenueShare',
  PpsRevenueShare = 'ppsRevenueShare',
  ModelAffiliate = 'modelAffiliate',
  StripchatModelAffiliate = 'stripchatModelAffiliate',
  StripchatStudioModelAffiliate = 'stripchatStudioModelAffiliate',
  Default = 'default',
  InternalTraffic = 'internalTraffic',
  MediaBuyer = 'mediaBuyer',
  MediaBuyerStudio = 'mediaBuyerStudio',
  MediaBuyerModel = 'mediaBuyerModel',
  PayPerClick = 'payPerClick',
  Unknown = 'unknown',
  MyClubDefault = 'myClubDefault',
  MyClubInternal = 'myClubInternal',
  MyClubRevenueShare = 'myClubRevenueShare',
  FaphouseDefault = 'faphouseDefault',
  LovescapeDefault = 'lovescapeDefault',
  LovescapeInternal = 'lovescapeInternal',
  LovescapeRevenueShare = 'lovescapeRevenueShare',
  LovescapePayPerFirstSubscription = 'lovescapePayPerFirstSubscription',
}

export enum SchemeId {
  Default = 1,
  InternalTraffic = 2,
  MediaBuyer = 3,
  PayPerClick = 10,
  PayPerFirstPurchase = 6,
  PayPerRegistration = 4,
  PayPerVerifiedRegistration = 5,
  RevenueShare = 7,
  PpsRevenueShare = 9,
  ModelAffiliate = 8,
  MediaBuyerStudio = 27,
  MediaBuyerModel = 28,
  StripchatModelAffiliate = 20,
  StripchatStudioModelAffiliate = 21,
  MyClubDefault = 11,
  MyClubInternal = 12,
  MyClubRevenueShare = 13,
  FaphouseDefault = 22,
  LovescapeDefault = 23,
  LovescapeInternal = 24,
  LovescapeRevenueShare = 25,
  LovescapePayPerFirstSubscription = 26,
}

export type Scheme = {
  id: number;
  schemeType: SchemeType;
  title: string;
  transactionRevenueRate: number;
  urls: Record<string, string>;
  balance: number;
  project: Project;
};

export enum Priority {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
}

export enum Project {
  Stripchat = 'stripchat',
  MyClub = 'myclub',
  Faphouse = 'faphouse',
  Lovescape = 'lovescape',
}

export enum NumericProject {
  Stripchat = 1,
  MyClub = 2,
  Faphouse = 3,
  Lovescape = 4,
}

export const PROJECT_BY_ID = {
  [NumericProject.Stripchat]: Project.Stripchat,
  [NumericProject.MyClub]: Project.MyClub,
  [NumericProject.Lovescape]: Project.Lovescape,
  [NumericProject.Faphouse]: Project.Faphouse,
};

export enum InterfaceViewType {
  Full = 'full',
  Limited = 'limited',
  LimitedWithEmail = 'limitedWithEmail',
}

export enum ReviewStatusType {
  Waiting = 'waiting',
  Approved = 'approved',
  Blocked = 'blocked',
  Contacted = 'contacted',
}

export enum ContactType {
  Discord = 'discord',
  Line = 'line',
  Signal = 'signal',
  Telegram = 'telegram',
  WhatsApp = 'whatsapp',
  Teams = 'teams',
}

export enum RolesForSignUp {
  AdNetworkOrMediaBuyer = 'adNetworkOrMediaBuyer',
  Publisher = 'publisher',
  ModelOrStudio = 'modelOrStudio',
  Other = 'other',
}

export enum RoleType {
  AdNetworkOrMediaBuyer = 'adNetworkOrMediaBuyer',
  Publisher = 'publisher',
  ModelOrStudio = 'modelOrStudio',
  Other = 'other',
  Lead = 'lead',
}

export enum VerificationType {
  Totp = 'totp',
  Disabled = 'disabled',
}

export enum Role {
  'Accountant' = 'accountant',
  'Admin' = 'admin',
  'Affiliate' = 'affiliate',
  'HeadManager' = 'headManager',
  'XhamsterManager' = 'xhamsterManager',
  'FaphouseManager' = 'faphouseManager',
  'Manager' = 'manager',
  'McManager' = 'mcManager',
  'HotLead' = 'lead',
  LsManager = 'lsManager',
  'TrialManager' = 'trialManager',
}

export type InviteModelUrls = Record<Project, string>;

export type User = {
  id: number;
  accountManagerId: number;
  username: string;
  isActive: boolean;
  email: string;
  isEmailVerified: boolean;
  clientRoleExtra: string;
  isAccountant: boolean;
  isAdmin: boolean;
  isHeadManager: boolean;
  isManager: boolean;
  isXhamsterManager: boolean;
  isFaphouseManager: boolean;
  isLsManager: boolean;
  isMcManager: boolean;
  isTrialManager: boolean;
  isBlocked: boolean;
  isApproved: boolean;
  isAffiliate: boolean;
  clientRole: RoleType;
  role: Role;
  clientCountry: string;
  contactExtra: string;
  contact: ContactType;
  languages: string[];
  clientHearFrom: string;
  clientFirstVisitFrom: string;
  isPayoutSettingsConfirmed: boolean;
  clientDevice: string;
  clientIp: string;
  createdAt: string;
  balance: number;
  priority: Priority;
  signUpsReached: boolean;
  firstBuyReached: boolean;
  payoutReached: boolean;
  showPayoutButton: boolean;
  trackId: string;
  inviteModelUrls: InviteModelUrls;
  inviteModelUrl: string;
  referralUrl: string;
  customTiers: [];
  modelsBonusThreshold: number;
  inviteRevenueRate: number;
  interfaceView: InterfaceViewType;
  reviewStatus: ReviewStatusType;
  isFraud: boolean;
  inviteId: number;
  inviterId: number;
  twoFactorAuth: VerificationType;
  project: {
    id: NumericProject;
    title: string;
    value: Project;
    withPaidScheme: boolean;
  };
  schemeType: SchemeType;
  projectId: NumericProject;
};

type SchemeTypeInfo = {
  scheme: Scheme;
  schemeIncomeHappened: boolean;
  hasApprovedPayouts: boolean;
};

export type ControlUser = User;
export type AffiliateUser = User & SchemeTypeInfo;
export type AffiliateOrControlUser = ControlUser | AffiliateUser;
